import { useState, useEffect } from 'react';
import { getAllProgress } from '../lib/db';
import type { ModuleId } from '../lib/types';
import { typingDrills, typingLessons } from '../modules/typing/data';
import { computerLessons } from '../modules/computer/data';
import { englishLessons } from '../modules/english/data';

interface ModuleCard {
  id: ModuleId;
  name: string;
  description: string;
  progress: number;
  completedLessons: number;
  totalLessons: number;
}

const moduleDefaults: Omit<ModuleCard, 'progress' | 'completedLessons' | 'totalLessons'>[] = [
  { id: 'typing', name: 'تایپ فارسی', description: 'یادگیری تایپ با صفحه‌کلید' },
  { id: 'computer', name: 'مهارت‌های کامپیوتر', description: 'کار با کامپیوتر و اینترنت' },
  { id: 'english', name: 'زبان انگلیسی', description: 'آموزش مقدماتی زبان انگلیسی' },
];

const moduleCatalog: Record<ModuleId, { totalLessons: number; lessonIds: Set<string> }> = {
  typing: {
    totalLessons: typingLessons.length + typingDrills.length,
    lessonIds: new Set([
      ...typingLessons.map((lesson) => lesson.id),
      ...typingDrills.map((drill) => drill.id),
    ]),
  },
  computer: {
    totalLessons: computerLessons.length,
    lessonIds: new Set(computerLessons.map((lesson) => lesson.id)),
  },
  english: {
    totalLessons: englishLessons.length,
    lessonIds: new Set(englishLessons.map((lesson) => lesson.id)),
  },
};

export default function Dashboard() {
  const [modules, setModules] = useState<ModuleCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  async function loadProgress() {
    try {
      const allProgress = await getAllProgress();
      const cards: ModuleCard[] = moduleDefaults.map((mod) => {
        const moduleInfo = moduleCatalog[mod.id];
        const completedLessons = allProgress.filter(
          (item) => item.moduleId === mod.id && moduleInfo.lessonIds.has(item.lessonId),
        ).length;
        const pct =
          moduleInfo.totalLessons > 0
            ? (completedLessons / moduleInfo.totalLessons) * 100
            : 0;
        return {
          ...mod,
          progress: pct,
          completedLessons,
          totalLessons: moduleInfo.totalLessons,
        };
      });
      setModules(cards);
    } catch {
      // Fallback with zero progress
      setModules(
        moduleDefaults.map((mod) => ({
          ...mod,
          progress: 0,
          completedLessons: 0,
          totalLessons: moduleCatalog[mod.id].totalLessons,
        })),
      );
    } finally {
      setLoading(false);
    }
  }

  function handleDownloadOffline() {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_CONTENT',
        urls: ['/', '/index.html', '/manifest.json', '/illustrations/logo.svg', '/sw.js'],
      });
      alert('محتوا برای استفاده آفلاین دانلود می‌شود');
    }
  }

  if (loading) {
    return <div className="dashboard-loading">در حال بارگذاری...</div>;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>سلام مامان! 👋</h1>
        <p className="dashboard-subtitle">امروز چی یاد بگیریم؟</p>
      </header>

      <section className="module-cards">
        {modules.map((mod) => (
          <div key={mod.id} className="module-card">
            <div className="module-card-header">
              <h3 className="module-name">{mod.name}</h3>
              <span className="module-progress-badge">
                {Math.round(mod.progress)}٪
              </span>
            </div>

            <p className="module-description">{mod.description}</p>

            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: `${mod.progress}%` }}
              />
            </div>

            <p className="module-progress-detail">
              {mod.completedLessons} از {mod.totalLessons} بخش تکمیل شده
            </p>

            <button className="module-continue-btn">
              ادامه
            </button>
          </div>
        ))}
      </section>

      <button className="download-offline-btn" onClick={handleDownloadOffline}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        دانلود برای آفلاین
      </button>
    </div>
  );
}
